<template>
  <section class="pin-faq">
    <div class="pin-faq__container">
      <header class="pin-faq__header">
        <h1>FAQ</h1>
        <p>Les réponses aux questions qu’on nous pose le plus souvent.</p>
      </header>

      <div class="pin-faq__grid">
        <article
          v-for="(item, index) in faqs"
          :key="index"
          class="pin-card"
          :class="{ 'is-open': openIndex === index }"
        >
          <button
            class="pin-card__q"
            type="button"
            :aria-expanded="openIndex === index"
            @click="toggle(index)"
          >
            <span class="pin-card__q-text">{{ item.question }}</span>

            <span class="pin-card__chev" aria-hidden="true">
              ⌄
            </span>
          </button>

          <transition name="pin-acc">
            <div v-show="openIndex === index" class="pin-card__a">
              <div v-if="item.isHtml" v-html="item.answer"></div>
              <div v-else>{{ item.answer }}</div>
            </div>
          </transition>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref } from "vue";

const openIndex = ref(null);

const toggle = (index) => {
  openIndex.value = openIndex.value === index ? null : index;
};

const faqs = ref([
  {
    question: "🧩 Le site est-il vraiment gratuit ?",
    answer:
      "Oui. Je propose ce service gratuitement pour les refuges et associations de protection animale. Aucune facturation, aucun frais caché. Mon objectif est de vous aider, pas de vous vendre quoi que ce soit.",
  },
  {
    question: "🔧 Peut-on modifier les animaux à l’adoption nous-mêmes ?",
    answer:
      "Oui. Je vous donne un accès simple pour ajouter, modifier ou retirer les animaux présentés sur votre site, sans connaissances techniques nécessaires.",
  },
  {
    question: "📞 Est-ce que vous assurez un suivi après la création du site ?",
    answer:
      "Oui. Je reste disponible pour toute question ou petit ajustement. Le but est que vous soyez autonome, mais jamais seul.",
  },
  {
    question: "🌐 Est-ce que le site sera bien référencé sur Google ?",
    answer:
      "Oui. Les sites sont optimisés pour le référencement naturel (SEO). Cela aide les gens à vous trouver facilement en ligne, notamment pour les adoptions.",
  },
  {
    question: "🎨 Peut-on personnaliser le design ou le contenu ?",
    answer:
      "Oui. Le site peut être adapté à vos couleurs, votre logo, vos textes… L’objectif est de refléter l'identité de votre refuge tout en restant clair et simple à utiliser.",
  },
  {
    question: "📸 Peut-on afficher des photos et détails pour chaque animal ?",
    answer:
      "Bien sûr. Chaque fiche animal peut contenir une ou plusieurs photos, une description, l'âge, le caractère, et toute autre information utile.",
  },
  {
    question: "💻 Faut-il avoir des compétences en informatique ?",
    answer:
      "Non. Je m’occupe de tout pour la mise en place. Et une fois en ligne, la gestion du site est pensée pour être la plus simple possible.",
  },
  {
    question: "🆚 Pourquoi choisir ce service plutôt qu’un site WordPress ou Wix ?",
    answer:
      "Parce que ce service est 100% pensé pour les refuges animaliers. WordPress ou Wix sont des outils généralistes. Il faut les configurer, les sécuriser, installer des plugins, choisir un thème, comprendre l’hébergement… Et souvent, le résultat est lent ou compliqué à gérer. Ici, tout est déjà prêt, optimisé et spécialisé : affichage automatique des animaux à l’adoption, design épuré et adapté aux refuges, pas de prise de tête technique. Vous n’avez rien à payer, rien à maintenir.",
  },
  {
    question: "🚀 Est-ce que le site est rapide et mobile-friendly ?",
    answer:
      "Oui. Tous les sites sont légers, rapides à charger, et s’adaptent automatiquement aux téléphones, tablettes et ordinateurs. De plus en plus de visiteurs consultent depuis leur mobile — votre site sera parfaitement lisible.",
  },
  {
    question: "🧠 Est-ce que vous comprenez les besoins d’un refuge ?",
    answer:
      "Oui. J’ai travaillé avec plusieurs refuges depuis 2020. Je connais les réalités du terrain : manque de temps, de moyens, de compétences web. Le site est fait pour vous faire gagner du temps, pas vous en faire perdre.",
  },
  {
    question: "⏱️ Quels sont les délais pour créer un site ?",
    answer:
      "Le site peut être prêt en 1 à 2 semaines, selon la complexité et votre réactivité (envoi des textes, photos, logo…). C’est rapide à mettre en place, car tout est déjà pensé pour les refuges. Vous n’avez qu’à fournir vos contenus, et je m’occupe du reste.",
  },
  {
    question: "🧡 Pourquoi ce service existe ?",
    answer:
      "Parce que chaque animal mérite une chance. Et parce que les associations manquent souvent de visibilité ou d’outils adaptés. Je ne suis pas une agence : je suis une personne qui agit concrètement pour une cause qui me tient à cœur.",
  },
  {
    question: "📬 Autre questions ? Contactez-nous",
    answer: `
      <p class="pin-contact">
        <strong>06 32 18 83 72</strong><br/>
        <a href="mailto:lesrefuges.animalier@gmail.com">lesrefuges.animalier@gmail.com</a>
      </p>
    `,
    isHtml: true,
  },
]);
</script>

<style scoped>
/* ==========
   Layout
========== */
.pin-faq {
  padding: 64px 16px;
}

.pin-faq__container {
  max-width: 1100px;
  margin: 0 auto;
}

.pin-faq__header {
  margin-bottom: 24px;
  text-align: center;
}

.pin-faq__header h2 {
  font-size: 34px;
  margin: 0 0 10px;
  letter-spacing: -0.02em;
}

.pin-faq__header p {
  margin: 0;
  opacity: 0.75;
  font-size: 15px;
}

/* ==========
   Pinterest grid
========== */
.pin-faq__grid {
  display: grid;
  grid-template-columns: 1fr; /* 1 seule colonne */
  gap: 18px;
}



/* ==========
   Card
========== */
.pin-card {
  break-inside: avoid;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 22px;
  margin: 0 0 18px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.07);
  border: 1px solid rgba(0, 0, 0, 0.06);
  overflow: hidden;
  backdrop-filter: blur(10px);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.pin-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 36px rgba(0, 0, 0, 0.09);
}

/* ==========
   Question button
========== */
.pin-card__q {
  width: 100%;
  padding: 16px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  cursor: pointer;
  border: none;
  outline: none;
  background: transparent;

  text-align: left;
}

.pin-card__q-text {
  font-weight: 700;
  font-size: 15px;
  line-height: 1.35;
}

.pin-card__chev {
  width: 34px;
  height: 34px;

  display: grid;
  place-items: center;

  border-radius: 50%;
  background: rgba(0, 0, 0, 0.06);

  font-size: 18px;
  transition: transform 0.25s ease;
}

.pin-card.is-open .pin-card__chev {
  transform: rotate(180deg);
}

/* ==========
   Answer
========== */
.pin-card__a {
  padding: 0 18px 18px;
  font-size: 14px;
  line-height: 1.6;
  opacity: 0.9;
}

/* contact style */
.pin-contact {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
}

.pin-contact a {
  text-decoration: none;
  font-weight: 700;
}

/* ==========
   Accordion animation
========== */
.pin-acc-enter-active,
.pin-acc-leave-active {
  transition: all 0.25s ease;
}

.pin-acc-enter-from,
.pin-acc-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
